import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '../../../lib/axios';
import { LoginCredentials, RegisterCredentials, AuthResponse, User } from '../types';
import { useAuthStore } from '../../../store/useAuthStore';

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};

const loginUser = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/login', credentials);
  return response.data;
};

const registerUser = async (credentials: RegisterCredentials): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/register', credentials);
  return response.data;
};

const logoutUser = async (): Promise<void> => {
  await apiClient.post('/logout');
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      toast.success(data.message || 'Successfully logged in!');
      useAuthStore.getState().setUser(data.user);
      queryClient.invalidateQueries({ queryKey: ['authUser'] });
    },
    onError: (error: any) => {
      if (error.response?.status !== 422) {
        toast.error(error.response?.data?.message || 'Failed to log in.');
      }
    }
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      toast.success('Logged out successfully.');
      useAuthStore.getState().clearAuth();
      queryClient.setQueryData(['authUser'], null);
      window.location.href = '/login';
    }
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: registerUser,
    onSuccess: (data) => {
      toast.success(data.message || 'Account created successfully!');
      useAuthStore.getState().setUser(data.user);
      queryClient.invalidateQueries({ queryKey: ['authUser'] });
    },
    onError: (error: any) => {
      if (error.response?.status !== 422) {
        toast.error(error.response?.data?.message || 'Failed to register.');
      }
    }
  });
};

export const useAuthUser = () => {
  return useQuery({
    queryKey: ['authUser'],
    queryFn: async (): Promise<User | null> => {
      const hasToken = !!localStorage.getItem('admin_token');

      if (!hasToken) {
        useAuthStore.getState().clearAuth();
        return null;
      }

      try {
        const response = await apiClient.get<User>('/user');
        useAuthStore.getState().setUser(response.data);
        return response.data;
      } catch (error) {
        useAuthStore.getState().clearAuth();
        return null;
      }
    },
    retry: false, 
    refetchOnWindowFocus: false, 
  });
};